import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getMessages, markAsRead, uploadMedia, reactToMessage, deleteMessage } from '../../services/api';
import { getSocket } from '../../services/socket';

// ─── Render plain text with **bold** markdown support ────────────────────────
function renderContent(text) {
  if (!text) return null;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <strong key={i} style={{ color: '#00ffb4', fontWeight: '700' }}>{part}</strong>
      : <span key={i}>{part}</span>
  );
}

// ─── Bulk broadcast card ─────────────────────────────────────────────────────
function BulkCard({ msg }) {
  return (
    <div style={bc.card}>
      {/* Gradient accent bar */}
      <div style={bc.accentBar} />

      {/* Image (if any) */}
      {msg.fileUrl && (
        <div style={bc.imgWrap}>
          <img
            src={msg.fileUrl}
            alt="broadcast"
            style={bc.img}
            onClick={() => window.open(msg.fileUrl, '_blank')}
          />
          <div style={bc.imgOverlay} />
          {/* Badge overlaid on image */}
          <div style={bc.badge}>
            <span style={bc.badgeDot} />
            BLIXO BROADCAST
          </div>
        </div>
      )}

      {/* Text body */}
      <div style={bc.body}>
        {/* Icon + title row */}
        <div style={bc.titleRow}>
          <span style={bc.megaIcon}>📣</span>
          <span style={bc.title}>{msg.bulkTitle || 'Announcement'}</span>
        </div>

        {/* Divider */}
        <div style={bc.divider} />

        {/* Description */}
        <p style={bc.desc}>{msg.content}</p>

        {/* Timestamp */}
        <div style={bc.footer}>
          <span style={bc.footerTime}>
            {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          <span style={bc.footerLabel}>Official Announcement</span>
        </div>
      </div>
    </div>
  );
}

const bc = {
  card: {
    position: 'relative',
    background: 'linear-gradient(145deg, #0d1a2a, #0a1520)',
    border: '1px solid rgba(0,255,180,0.2)',
    borderRadius: '18px',
    overflow: 'hidden',
    maxWidth: '300px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(0,255,180,0.05)',
  },
  accentBar: {
    height: '3px',
    background: 'linear-gradient(90deg, #00ffb4, #00d4ff, #7b61ff)',
  },
  imgWrap: {
    position: 'relative',
    width: '100%',
    height: '160px',
    overflow: 'hidden',
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    cursor: 'pointer',
    transition: 'transform 0.3s',
    display: 'block',
  },
  imgOverlay: {
    position: 'absolute',
    bottom: 0, left: 0, right: 0,
    height: '60px',
    background: 'linear-gradient(transparent, rgba(8,12,20,0.9))',
  },
  badge: {
    position: 'absolute',
    top: '10px', left: '10px',
    background: 'rgba(0,255,180,0.15)',
    border: '1px solid rgba(0,255,180,0.35)',
    backdropFilter: 'blur(8px)',
    borderRadius: '20px',
    padding: '3px 10px',
    fontSize: '9px',
    fontWeight: '800',
    color: '#00ffb4',
    letterSpacing: '1.5px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  badgeDot: {
    width: '5px', height: '5px',
    borderRadius: '50%',
    background: '#00ffb4',
    boxShadow: '0 0 6px #00ffb4',
    display: 'inline-block',
  },
  body: {
    padding: '14px 16px 12px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px',
  },
  megaIcon: {
    fontSize: '18px',
    lineHeight: 1,
  },
  title: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#e2e8f0',
    fontFamily: "'Rajdhani', sans-serif",
    letterSpacing: '0.5px',
    lineHeight: 1.2,
  },
  divider: {
    height: '1px',
    background: 'linear-gradient(90deg, rgba(0,255,180,0.3), transparent)',
    marginBottom: '10px',
  },
  desc: {
    margin: 0,
    fontSize: '13px',
    color: 'rgba(255,255,255,0.55)',
    lineHeight: '1.6',
    marginBottom: '12px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerTime: {
    fontSize: '10px',
    color: 'rgba(255,255,255,0.25)',
  },
  footerLabel: {
    fontSize: '9px',
    color: 'rgba(0,255,180,0.5)',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
};

// ─── Main Chat component ─────────────────────────────────────────────────────
export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState('');
  const [loading, setLoading]         = useState(true);
  const [uploading, setUploading]     = useState(false);
  const [adminTyping, setAdminTyping] = useState(false);
  const [replyTo, setReplyTo]         = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [caption, setCaption]         = useState('');
  const [page, setPage]               = useState(1);
  const [hasMore, setHasMore]         = useState(false);
  const bottomRef  = useRef(null);
  const fileRef    = useRef(null);
  const inputRef   = useRef(null);
  const typingTimer = useRef(null);

  const loadMessages = useCallback(async (p = 1) => {
    try {
      const { data } = await getMessages(user._id, p);
      const msgs = data.messages || [];
      if (p === 1) setMessages(msgs);
      else setMessages(prev => [...msgs, ...prev]);
      setHasMore(data.hasMore || false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user._id]);

  useEffect(() => {
    loadMessages(1);
    markAsRead(user._id).catch(() => {});

    const socket = getSocket();
    if (!socket) return;

    socket.emit('user:join', { userId: user._id });

    socket.on('message:received', (msg) => {
      setMessages(prev => [...prev, msg]);
      markAsRead(user._id).catch(() => {});
    });

    socket.on('message:status', ({ messageId, status }) => {
      setMessages(prev => prev.map(m => m._id === messageId ? { ...m, status } : m));
    });

    socket.on('message:allRead', () => {
      setMessages(prev => prev.map(m => ({ ...m, status: 'read' })));
    });

    socket.on('typing:start', () => setAdminTyping(true));
    socket.on('typing:stop',  () => setAdminTyping(false));

    return () => {
      socket.off('message:received');
      socket.off('message:status');
      socket.off('message:allRead');
      socket.off('typing:start');
      socket.off('typing:stop');
    };
  }, [loadMessages, user._id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    const socket = getSocket();
    if (!socket) return;

    socket.emit('message:send', {
      senderId:   user._id,
      senderType: 'user',
      userId:     user._id,
      type:       'text',
      content:    text,
      replyTo:    replyTo?._id || null,
    });

    setInput('');
    setReplyTo(null);
    inputRef.current?.focus();
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); return; }
    const socket = getSocket();
    if (socket) {
      socket.emit('typing:start', { userId: user._id });
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => socket.emit('typing:stop', { userId: user._id }), 1500);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    const previewUrl = (isImage || isVideo) ? URL.createObjectURL(file) : null;
    setPendingFile({ file, previewUrl, isImage, isVideo });
    setCaption('');
    e.target.value = '';
  };

  const handleSendFile = async () => {
    if (!pendingFile || !user?._id) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile.file);
      const { data } = await uploadMedia(fd);
      const socket = getSocket();
      if (socket) {
        socket.emit('message:send', {
          senderId:   user._id,
          senderType: 'user',
          userId:     user._id,
          type:       data.fileType || 'document',
          content:    caption.trim(),
          fileUrl:    data.fileUrl,
          fileName:   data.fileName,
          fileSize:   data.fileSize,
          replyTo:    replyTo?._id || null,
        });
      }
      setPendingFile(null);
      setCaption('');
      setReplyTo(null);
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleReact  = (messageId, emoji) => reactToMessage(messageId, emoji).catch(() => {});
  const handleDelete = (messageId) => {
    deleteMessage(messageId, true).then(() => {
      setMessages(prev => prev.map(m =>
        m._id === messageId ? { ...m, isDeletedForEveryone: true, content: '' } : m
      ));
    }).catch(() => {});
  };

  if (loading) return <div style={s.loading}>Loading chat...</div>;

  return (
    <div style={s.wrap}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerAvatarWrap}>
          <div style={s.headerAvatar}>AD</div>
          <div style={s.onlineDot} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={s.headerName}>MegaFishHub Support</div>
          <div style={s.headerStatus}>
            {adminTyping
              ? <span style={{ color: '#00ffb4' }}>✏️ Typing...</span>
              : <span style={{ color: 'rgba(255,255,255,.3)' }}>🟢 Online — we reply instantly</span>}
          </div>
        </div>
      </div>

      {/* Load more */}
      {hasMore && (
        <div style={{ padding: '8px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <button style={s.loadMoreBtn} onClick={() => { const p = page + 1; setPage(p); loadMessages(p); }}>
            ↑ Load older messages
          </button>
        </div>
      )}

      {/* Messages */}
      <div style={s.messages}>
        {messages.length === 0 && (
          <div style={s.emptyMsg}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>👋</div>
            <div style={s.emptyTitle}>Welcome to MegaFishHub Support!</div>
            <div style={s.emptySub}>Send a message and we'll get back to you shortly.</div>
          </div>
        )}

        {(() => {
          const out = []; let last = null;
          messages.forEach(msg => {
            const d = new Date(msg.createdAt);
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            const lbl = d.toDateString()===today.toDateString() ? 'Today' : d.toDateString()===yesterday.toDateString() ? 'Yesterday' : d.toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'});
            if (lbl !== last) { last = lbl; out.push(<div key={lbl+msg._id} style={s.dateDivider}><div style={s.dateLine}/><span style={s.dateLabel}>{lbl}</span><div style={s.dateLine}/></div>); }
            out.push(msg);
          });
          return out;
        })().map((item, i) => {
          if (!item._id) return item;
          const msg = item;
          const isOwn     = msg.senderType === 'user';
          const isDeleted = msg.isDeletedForEveryone;
          const isBulk    = msg.type === 'bulk';

          return (
            <div
              key={msg._id}
              style={{
                display: 'flex', flexDirection: 'column',
                alignItems: isBulk ? 'flex-start' : isOwn ? 'flex-end' : 'flex-start',
                marginBottom: '6px', padding: '0 14px',
              }}
            >
              {/* Reply preview */}
              {msg.replyTo && (
                <div style={{ ...s.replyPreview, alignSelf: isOwn ? 'flex-end' : 'flex-start' }}>
                  ↩ {msg.replyTo.content?.slice(0, 40) || 'Media'}...
                </div>
              )}

              {/* Bulk broadcast card — special full-width treatment */}
              {isBulk && !isDeleted ? (
                <BulkCard msg={msg} />
              ) : (
                <div style={{
                  ...s.bubble,
                  ...(isOwn ? s.bubbleOwn : s.bubbleOther),
                  ...(isDeleted ? { opacity: 0.5 } : {}),
                }}>
                  {isDeleted ? (
                    <span style={{ color: 'rgba(255,255,255,.3)', fontSize: '12px', fontStyle: 'italic' }}>🚫 Message deleted</span>
                  ) : msg.type === 'image' ? (
                    <div>
                      <img src={msg.fileUrl} alt="img" style={{ maxWidth: '220px', borderRadius: '10px', cursor: 'pointer', display: 'block' }} onClick={() => window.open(msg.fileUrl, '_blank')} />
                      {msg.content && <span style={{ ...s.msgText, display: 'block', marginTop: '6px', fontSize: '13px' }}>{msg.content}</span>}
                    </div>
                  ) : msg.type === 'video' ? (
                    <div>
                      <video src={msg.fileUrl} controls style={{ maxWidth: '220px', borderRadius: '10px', display: 'block' }} />
                      {msg.content && <span style={{ ...s.msgText, display: 'block', marginTop: '6px', fontSize: '13px' }}>{msg.content}</span>}
                    </div>
                  ) : msg.type === 'audio' ? (
                    <audio src={msg.fileUrl} controls style={{ maxWidth: '220px' }} />
                  ) : msg.type === 'document' ? (
                    <div>
                      <a href={msg.fileUrl} target="_blank" rel="noreferrer" style={{ color: '#00ffb4', fontSize: '13px' }}>📎 {msg.fileName || 'Download'}</a>
                      {msg.content && <span style={{ ...s.msgText, display: 'block', marginTop: '6px', fontSize: '13px' }}>{msg.content}</span>}
                    </div>
                  ) : (
                    <span style={s.msgText}>{renderContent(msg.content)}</span>
                  )}
                  <div style={s.msgMeta}>
                    <span style={s.msgTime}>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    {isOwn && <span style={{ fontSize: '11px', color: 'rgba(0,255,180,.6)' }}>{msg.status === 'read' ? '✓✓' : '✓'}</span>}
                  </div>
                </div>
              )}

              {/* Reactions */}
              {msg.reactions?.length > 0 && (
                <div style={{ display: 'flex', gap: '4px', marginTop: '3px' }}>
                  {msg.reactions.map((r, i) => (
                    <span key={i} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', borderRadius: '10px', padding: '2px 6px', fontSize: '12px' }}>{r.emoji}</span>
                  ))}
                </div>
              )}

              {/* Action buttons (hide on bulk cards) */}
              {!isDeleted && !isBulk && (
                <div style={{ display: 'flex', gap: '3px', marginTop: '3px', justifyContent: isOwn ? 'flex-end' : 'flex-start' }}>
                  <button style={s.actionBtn} onClick={() => setReplyTo(msg)}>↩</button>
                  {['👍','❤️','😂','😮'].map(em => (
                    <button key={em} style={s.actionBtn} onClick={() => handleReact(msg._id, em)}>{em}</button>
                  ))}
                  {isOwn && <button style={{ ...s.actionBtn, color: '#ff6b6b' }} onClick={() => handleDelete(msg._id)}>🗑️</button>}
                </div>
              )}
            </div>
          );
        })}

        {adminTyping && (
          <div style={{ padding: '4px 14px' }}>
            <div style={s.typingBubble}>
              <span style={s.typingDot} /><span style={s.typingDot} /><span style={s.typingDot} />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Media preview modal */}
      {pendingFile && (
        <div style={s.mediaModal}>
          <div style={s.mediaModalBox}>
            <div style={s.mediaModalHeader}>
              <span style={s.mediaModalTitle}>Send Media</span>
              <button style={s.mediaModalClose} onClick={() => { setPendingFile(null); setCaption(''); }}>✕</button>
            </div>
            <div style={s.mediaPreviewWrap}>
              {pendingFile.isImage && (
                <img src={pendingFile.previewUrl} alt="preview" style={s.mediaPreviewImg} />
              )}
              {pendingFile.isVideo && (
                <video src={pendingFile.previewUrl} controls style={s.mediaPreviewImg} />
              )}
              {!pendingFile.isImage && !pendingFile.isVideo && (
                <div style={s.mediaPreviewDoc}>
                  <span style={{ fontSize: '40px' }}>📄</span>
                  <span style={s.mediaPreviewDocName}>{pendingFile.file.name}</span>
                </div>
              )}
            </div>
            <div style={s.captionWrap}>
              <input
                style={s.captionInput}
                placeholder="Add a caption... (optional)"
                value={caption}
                onChange={e => setCaption(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSendFile(); }}
                autoFocus
              />
            </div>
            <div style={s.mediaModalActions}>
              <button style={s.mediaCancelBtn} onClick={() => { setPendingFile(null); setCaption(''); }}>
                Cancel
              </button>
              <button style={s.mediaSendBtn} onClick={handleSendFile} disabled={uploading}>
                {uploading ? '⏳ Sending...' : '➤  Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply bar */}
      {replyTo && (
        <div style={s.replyBar}>
          <div>
            <div style={s.replyLabel}>↩ Replying to</div>
            <div style={s.replyText}>{replyTo.content?.slice(0, 50) || 'Media'}...</div>
          </div>
          <button style={s.replyClose} onClick={() => setReplyTo(null)}>✕</button>
        </div>
      )}

      {/* Input */}
      <div style={s.inputArea}>
        <button style={s.attachBtn} onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? '⏳' : '📎'}
        </button>
        <input type="file" ref={fileRef} style={{ display: 'none' }} onChange={handleFile}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx" />
        <textarea
          ref={inputRef}
          style={s.input}
          placeholder="Type a message..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
        />
        <button style={{ ...s.sendBtn, opacity: input.trim() ? 1 : 0.4 }}
          onClick={handleSend} disabled={!input.trim()}>➤</button>
      </div>
    </div>
  );
}

const s = {
  wrap:             { display: 'flex', flexDirection: 'column', height: '100%', background: '#080c14', overflow: 'hidden' },
  loading:          { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,.3)', fontSize: '14px', background: '#080c14' },
  header:           { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#0d1420', borderBottom: '1px solid rgba(0,255,180,.08)', flexShrink: 0 },
  headerAvatarWrap: { position: 'relative', flexShrink: 0 },
  headerAvatar:     { width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg,#00ffb4,#00d4ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#080c14', fontSize: '14px', fontWeight: '900', fontFamily: "'Rajdhani',sans-serif" },
  onlineDot:        { position: 'absolute', bottom: '-1px', right: '-1px', width: '11px', height: '11px', borderRadius: '50%', background: '#00ffb4', border: '2px solid #0d1420' },
  headerName:       { color: '#e2e8f0', fontSize: '15px', fontWeight: '700', fontFamily: "'Rajdhani',sans-serif" },
  headerStatus:     { fontSize: '11px', marginTop: '2px' },
  loadMoreBtn:      { background: 'rgba(0,255,180,.06)', border: '1px solid rgba(0,255,180,.15)', borderRadius: '9px', color: 'rgba(0,255,180,.7)', padding: '6px 16px', cursor: 'pointer', fontSize: '12px', fontFamily: "'Exo 2',sans-serif" },
  messages:         { flex: 1, overflowY: 'auto', padding: '16px 0', display: 'flex', flexDirection: 'column' },
  emptyMsg:         { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center' },
  emptyTitle:       { color: 'rgba(255,255,255,.4)', fontSize: '15px', fontWeight: '600' },
  emptySub:         { color: 'rgba(255,255,255,.25)', fontSize: '13px', marginTop: '6px' },
  bubble:           { maxWidth: '75%', padding: '10px 14px', borderRadius: '16px', wordBreak: 'break-word' },
  bubbleOwn:        { background: 'linear-gradient(135deg,rgba(0,255,180,.15),rgba(0,212,255,.1))', border: '1px solid rgba(0,255,180,.2)', borderBottomRightRadius: '4px' },
  bubbleOther:      { background: '#0d1420', border: '1px solid rgba(0,255,180,.08)', borderBottomLeftRadius: '4px' },
  msgText:          { color: '#e2e8f0', fontSize: '14px', lineHeight: '1.5' },
  msgMeta:          { display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', justifyContent: 'flex-end' },
  msgTime:          { fontSize: '10px', color: 'rgba(255,255,255,.25)' },
  replyPreview:     { background: 'rgba(0,255,180,.05)', border: '1px solid rgba(0,255,180,.1)', borderRadius: '8px', padding: '4px 10px', fontSize: '11px', color: 'rgba(255,255,255,.35)', marginBottom: '4px', maxWidth: '75%' },
  typingBubble:     { display: 'flex', gap: '4px', padding: '10px 14px', background: '#0d1420', border: '1px solid rgba(0,255,180,.08)', borderRadius: '16px', borderBottomLeftRadius: '4px', width: 'fit-content', alignItems: 'center' },
  typingDot:        { width: '6px', height: '6px', borderRadius: '50%', background: 'rgba(0,255,180,.5)', display: 'inline-block' },
  replyBar:         { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,255,180,.05)', borderTop: '1px solid rgba(0,255,180,.1)', padding: '8px 14px', flexShrink: 0 },
  replyLabel:       { color: 'rgba(0,255,180,.7)', fontSize: '10px', fontWeight: '700', letterSpacing: '1px' },
  replyText:        { color: 'rgba(255,255,255,.4)', fontSize: '12px' },
  replyClose:       { background: 'transparent', border: 'none', color: 'rgba(255,255,255,.3)', cursor: 'pointer', fontSize: '14px', padding: '4px 8px' },
  inputArea:        { display: 'flex', alignItems: 'flex-end', gap: '8px', padding: '10px 12px', background: '#0d1420', borderTop: '1px solid rgba(0,255,180,.08)', flexShrink: 0 },
  attachBtn:        { background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: '10px', color: 'rgba(255,255,255,.4)', fontSize: '18px', cursor: 'pointer', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  input:            { flex: 1, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(0,255,180,.15)', borderRadius: '12px', padding: '10px 14px', color: '#fff', fontSize: '14px', fontFamily: "'Exo 2',sans-serif", outline: 'none', resize: 'none', minHeight: '40px', maxHeight: '120px', overflowY: 'auto', lineHeight: '1.4' },
  sendBtn:          { background: 'linear-gradient(135deg,#00ffb4,#00d4ff)', border: 'none', borderRadius: '10px', color: '#080c14', fontSize: '18px', cursor: 'pointer', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'opacity .2s' },
  actionBtn:        { background: 'rgba(255,255,255,.04)', border: 'none', borderRadius: '6px', color: 'rgba(255,255,255,.4)', cursor: 'pointer', fontSize: '12px', padding: '2px 5px' },
  dateDivider: { display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px' },
  dateLine: { flex:1, height:'1px', background:'rgba(255,255,255,0.06)' },
  dateLabel: { color:'rgba(255,255,255,0.2)', fontSize:'11px', fontWeight:'600', letterSpacing:'1px', padding:'3px 12px', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.06)', whiteSpace:'nowrap' },
  mediaModal:         { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  mediaModalBox:      { background: '#0d1420', border: '1px solid rgba(0,255,180,0.15)', borderRadius: '24px 24px 0 0', padding: '20px', width: '100%', maxWidth: '520px', boxShadow: '0 -20px 60px rgba(0,0,0,0.6)' },
  mediaModalHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  mediaModalTitle:    { color: '#e2e8f0', fontSize: '16px', fontWeight: '700', fontFamily: "'Rajdhani',sans-serif", letterSpacing: '1px' },
  mediaModalClose:    { background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: '8px', color: 'rgba(255,255,255,0.4)', fontSize: '16px', cursor: 'pointer', width: '32px', height: '32px' },
  mediaPreviewWrap:   { borderRadius: '14px', overflow: 'hidden', marginBottom: '14px', background: 'rgba(0,0,0,0.3)', maxHeight: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  mediaPreviewImg:    { width: '100%', maxHeight: '260px', objectFit: 'contain', display: 'block' },
  mediaPreviewDoc:    { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '32px' },
  mediaPreviewDocName:{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', textAlign: 'center', wordBreak: 'break-all' },
  captionWrap:        { marginBottom: '14px' },
  captionInput:       { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,255,180,0.15)', borderRadius: '12px', padding: '12px 16px', color: '#fff', fontSize: '14px', fontFamily: "'Exo 2',sans-serif", outline: 'none', boxSizing: 'border-box' },
  mediaModalActions:  { display: 'flex', gap: '10px' },
  mediaCancelBtn:     { flex: 1, padding: '13px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: 'rgba(255,255,255,0.4)', fontSize: '14px', cursor: 'pointer', fontFamily: "'Exo 2',sans-serif" },
  mediaSendBtn:       { flex: 2, padding: '13px', borderRadius: '12px', border: 'none', background: 'linear-gradient(90deg,#00ffb4,#00d4ff)', color: '#080c14', fontSize: '15px', fontWeight: '900', cursor: 'pointer', fontFamily: "'Rajdhani',sans-serif", letterSpacing: '1px' },
};
